import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Link, Paper, Typography } from '@mui/material';
import { ResponseCodes } from '@provider-portal/common/api/responseCodes';
import { consoleError } from '@provider-portal/common/utils/utils';

import {
  credentialsAuthAsync,
  selectAuthError,
  selectAuthRequestState,
} from '@bolteu/redux-session';

import { selectAccountRequestState } from 'rdx/account';

import LoginForm from 'components/LoginForm/LoginForm';
import { useTitle } from 'root/navigation/useTitle';

export interface LoginUrlParams {
  token: string;
}

export enum LoginErrorType {
  BAD_CREDENTIALS = 'bad_credentials',
  ACCOUNT_LOCKED = 'account_locked',
  UNKNOWN = 'unkonwn',
}

const rawErrorToLoginError = (error: Error | undefined): LoginErrorType | undefined => {
  if (!error) {
    return undefined;
  }
  if (error.message.includes(ResponseCodes.PROVIDER_ACCOUNT_INVALID_CREDENTIALS.toString())) {
    return LoginErrorType.BAD_CREDENTIALS;
  }
  if (error.message.includes(ResponseCodes.PROVIDER_ACCOUNT_LOCKED.toString())) {
    return LoginErrorType.ACCOUNT_LOCKED;
  }

  consoleError('Unknown login error occured, please add an explicit handler', { error });
  return LoginErrorType.UNKNOWN;
};

export const Login = () => {
  useTitle('screens.Login.login');
  const intl = useIntl();
  const dispatch = useDispatch();
  const authError = useSelector(selectAuthError);
  const authRequestState = useSelector(selectAuthRequestState);
  const accountRequestState = useSelector(selectAccountRequestState);

  const strings = useMemo(
    () => ({
      wrongCredentials: intl.formatMessage({ id: 'screens.Login.wrongCredentials' }),
      accountLocked: intl.formatMessage({ id: 'screens.Login.accountLocked' }),
      username: intl.formatMessage({ id: 'screens.Login.username' }),
      password: intl.formatMessage({ id: 'screens.Login.password' }),
      rememberMe: intl.formatMessage({ id: 'screens.Login.rememberMe' }),
      login: intl.formatMessage({ id: 'screens.Login.login' }),
    }),
    [intl],
  );

  const onLoginSubmitHandler = useCallback(
    (payload) => {
      dispatch(credentialsAuthAsync.request(payload));
    },
    [dispatch],
  );

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Box sx={{ maxWidth: '450px', width: '85vw' }}>
          <Paper>
            <Box py={4} px={3}>
              <Typography variant="h6">
                {intl.formatMessage({ id: 'screens.Login.title' })}
              </Typography>
              <Box>
                <LoginForm
                  strings={strings}
                  onSubmit={onLoginSubmitHandler}
                  authRequestState={authRequestState}
                  accountRequestState={accountRequestState}
                  authError={rawErrorToLoginError(authError)}
                />
                <Box mt={2}>
                  <Link href="/forgotPassword" variant="bodyM">
                    {intl.formatMessage({ id: 'screens.Login.forgotPassword' })}
                  </Link>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
};
